import * as THREE from 'three'
import type {
  PointCloudOctree,
  PointCloudOctreeNode,
  ViewLodProgressContext,
} from '@/plugins/pointCloud/core/PointCloudOctree'
import { ClipMethod, ClipTask } from '@/shared/constants'
import { getLoggerManager, LogLevel } from '@/shared/logging'
import { BinaryHeap } from '@/shared/utils/BinaryHeap'
import { Box3Helper } from '@/shared/utils/Box3Helper'
import type { OctreeGeometryNode } from './OctreeGeometry'

const log = getLoggerManager().getLogger('pointcloud:visibility')
const VISIBILITY_DEBUG_LOG_INTERVAL_MS = 15000
let lastVisibilityDebugLogTime = 0

/** 可见性计算所需的最小驻留顺序接口。 */
interface LRULike {
  touch(node: OctreeGeometryNode): void
}

interface DEMUpdatable {
  update(nodes: PointCloudOctreeNode[]): void
}

export interface PointCloudVisibilityViewport {
  width: number
  height: number
}

interface HeapItem<T extends OctreeGeometryNode | PointCloudOctreeNode> {
  pointcloud: number // 对应 pointclouds 数组索引
  node: T // 八叉树节点类型（需根据实际类型替换）
  weight: number // 用于堆排序的权重值
  parent?: T
}

interface PointCloudTransformVersion {
  number: number
  transform: THREE.Matrix4
}

interface VisibilityDebugStats {
  visited: number
  visibleCandidates: number
  rejectedFrustum: number
  rejectedGlobalBudget: number
  rejectedPointcloudBudget: number
  rejectedMaxLevel: number
  forcedLevelVisible: number
  budgetBreaks: number
  geometryCandidates: number
  loadedGeometryCandidates: number
  gpuUploads: number
  gpuLimitHits: number
  queuedForLoad: number
  parentNotTree: number
  loadedWaitingForParent: number
  queuedWaitingForParent: number
  permanentlyFailed: number
  loadBlockedLoaded: number
  loadBlockedLoading: number
  loadBlockedDisposed: number
  loadBlockedRetry: number
  loadBlockedConcurrency: number
  loadBlockedNoLoader: number
  loadBlockedUnknown: number
  treeVisible: number
  childrenQueued: number
  childrenSkippedMinSize: number
}

export interface VisibilityActivitySnapshot {
  queuedForLoad: number
  loadsStarted: number
  gpuUploads: number
  gpuUploadBacklog: number
  loadedWaitingForParent: number
  queuedWaitingForParent: number
  queueSize: number
}

function createVisibilityDebugStats(): VisibilityDebugStats {
  return {
    visited: 0,
    visibleCandidates: 0,
    rejectedFrustum: 0,
    rejectedGlobalBudget: 0,
    rejectedPointcloudBudget: 0,
    rejectedMaxLevel: 0,
    forcedLevelVisible: 0,
    budgetBreaks: 0,
    geometryCandidates: 0,
    loadedGeometryCandidates: 0,
    gpuUploads: 0,
    gpuLimitHits: 0,
    queuedForLoad: 0,
    parentNotTree: 0,
    loadedWaitingForParent: 0,
    queuedWaitingForParent: 0,
    permanentlyFailed: 0,
    loadBlockedLoaded: 0,
    loadBlockedLoading: 0,
    loadBlockedDisposed: 0,
    loadBlockedRetry: 0,
    loadBlockedConcurrency: 0,
    loadBlockedNoLoader: 0,
    loadBlockedUnknown: 0,
    treeVisible: 0,
    childrenQueued: 0,
    childrenSkippedMinSize: 0,
  }
}

function recordLoadBlockedReason(node: OctreeGeometryNode, stats: VisibilityDebugStats): void {
  if (node.loaded) {
    stats.loadBlockedLoaded++
    return
  }
  if (node.loading) {
    stats.loadBlockedLoading++
    return
  }
  const signal = node.octreeGeometry.loadSignal
  if (node.disposed || node.octreeGeometry.disposed || signal?.aborted) {
    stats.loadBlockedDisposed++
    return
  }
  if (node.loadPermanentlyFailed) {
    stats.permanentlyFailed++
    return
  }
  if (node.loadFailed && getLoggerManager().now() < node.retryAfter) {
    stats.loadBlockedRetry++
    return
  }
  if (node.Potree.numNodesLoading >= node.Potree.maxNodesLoading) {
    stats.loadBlockedConcurrency++
    return
  }
  if (!node.octreeGeometry.loader) {
    stats.loadBlockedNoLoader++
    return
  }
  stats.loadBlockedUnknown++
}

function shouldLogVisibilityDebugStats(
  stats: VisibilityDebugStats,
  loadsStarted: number,
  queueSize: number
): boolean {
  return (
    loadsStarted > 0 ||
    queueSize > 0 ||
    stats.queuedForLoad > 0 ||
    stats.gpuUploads > 0 ||
    stats.gpuLimitHits > 0 ||
    stats.loadBlockedDisposed > 0 ||
    stats.loadBlockedRetry > 0 ||
    stats.loadBlockedConcurrency > 0 ||
    stats.loadBlockedNoLoader > 0 ||
    stats.loadBlockedUnknown > 0 ||
    stats.rejectedGlobalBudget > 0 ||
    stats.rejectedPointcloudBudget > 0 ||
    stats.budgetBreaks > 0 ||
    stats.permanentlyFailed > 0
  )
}

function isOctreeGeometryNode(
  node: OctreeGeometryNode | PointCloudOctreeNode
): node is OctreeGeometryNode {
  return 'isGeometryNode' in node && node.isGeometryNode()
}

function isPointCloudOctreeNode(
  node: OctreeGeometryNode | PointCloudOctreeNode
): node is PointCloudOctreeNode {
  return 'isTreeNode' in node && node.isTreeNode()
}

function hasDEMUpdater(
  pointcloud: PointCloudOctree
): pointcloud is PointCloudOctree & { dem: DEMUpdatable } {
  return (
    pointcloud.generateDEM && pointcloud.dem != null && typeof pointcloud.dem.update === 'function'
  )
}

/** 按实际发起的加载数填满预算，退避中或永久失败的节点不会消耗预算槽。 */
export function startNodeLoads(
  unloadedGeometry: OctreeGeometryNode[],
  maxNodesLoading: number,
  debugStats?: VisibilityDebugStats | null
): number {
  let nodesLoadingStarted = 0
  for (let i = 0; i < unloadedGeometry.length && nodesLoadingStarted < maxNodesLoading; i++) {
    if (unloadedGeometry[i].load()) {
      nodesLoadingStarted++
    } else if (debugStats) {
      recordLoadBlockedReason(unloadedGeometry[i], debugStats)
    }
  }

  return nodesLoadingStarted
}

const _pointcloudTransformVersion = new WeakMap<PointCloudOctree, PointCloudTransformVersion>()

/**
 * 估算正交相机下每物体单位对应的最大屏幕像素长度。
 *
 * @param objectToClip - 从物体空间到裁剪空间的复合矩阵
 * @param domWidth - 视口宽度，像素
 * @param domHeight - 视口高度，像素
 * @returns 单位向量经变换后的最大像素长度
 */
export function computeOrthographicPixelScale(
  objectToClip: THREE.Matrix4,
  domWidth: number,
  domHeight: number
): number {
  const origin = new THREE.Vector3(0, 0, 0).applyMatrix4(objectToClip)
  const axes = [
    new THREE.Vector3(1, 0, 0).applyMatrix4(objectToClip),
    new THREE.Vector3(0, 1, 0).applyMatrix4(objectToClip),
    new THREE.Vector3(0, 0, 1).applyMatrix4(objectToClip),
  ]

  let maxPixelScale = 0
  for (const axis of axes) {
    const dx = (axis.x - origin.x) * domWidth * 0.5
    const dy = (axis.y - origin.y) * domHeight * 0.5
    maxPixelScale = Math.max(maxPixelScale, Math.hypot(dx, dy))
  }

  return maxPixelScale
}

/**
 * 更新点云可见性，计算可见节点并更新材质。
 *
 * @param pointclouds - 待更新的八叉树点云列表
 * @param camera - 当前相机
 * @param lru - LRU 缓存实例
 * @param pointBudget - 全局点数预算
 * @param maxNodesLoading - 每帧最大加载节点数
 * @param viewportSize - 当前 View 的逻辑像素尺寸
 * @param progressContext - 可选帧与视口上下文，用于聚合 LOD 就绪度
 * @returns 可见性计算结果，含可见节点、活动快照、必需节点和待加载候选
 */
export function updatePointClouds(
  pointclouds: PointCloudOctree[],
  camera,
  lru: LRULike,
  pointBudget: number,
  maxNodesLoading: number,
  viewportSize: PointCloudVisibilityViewport,
  progressContext?: ViewLodProgressContext
) {
  for (const pointcloud of pointclouds) {
    const start = performance.now()

    for (const profileRequest of pointcloud.profileRequests) {
      profileRequest.update()

      const duration = performance.now() - start
      if (duration > 5) {
        break
      }
    }
  }

  const result = updateVisibility(
    pointclouds,
    camera,
    lru,
    pointBudget,
    maxNodesLoading,
    viewportSize,
    progressContext
  )

  for (const pointcloud of pointclouds) {
    pointcloud.updateMaterial(pointcloud.material, pointcloud.visibleNodes, camera, viewportSize)
    pointcloud.updateVisibleBounds()
  }

  return result
}

/**
 * 初始化每个点云的可见性遍历结构：视椎体、相机对象空间位置、优先队列等。
 *
 * @param pointclouds - 待处理的八叉树点云列表
 * @param camera - 当前相机
 * @param viewportSize - 当前 View 的逻辑像素尺寸
 * @returns `{ frustums, camObjPositions, orthoPixelScales, priorityQueue }`
 */
export function updateVisibilityStructures(
  pointclouds: PointCloudOctree[],
  camera,
  viewportSize: PointCloudVisibilityViewport
) {
  const frustums: THREE.Frustum[] = []
  const camObjPositions: THREE.Vector3[] = []
  const orthoPixelScales: number[] = []
  const priorityQueue = new BinaryHeap<HeapItem<OctreeGeometryNode | PointCloudOctreeNode>>(
    (x) => 1 / x.weight
  )
  const domWidth = Math.max(1, viewportSize.width)
  const domHeight = Math.max(1, viewportSize.height)

  for (let i = 0; i < pointclouds.length; i++) {
    const pointcloud = pointclouds[i]

    if (!pointcloud.initialized()) {
      continue
    }

    for (const visibleNode of pointcloud.visibleNodes) {
      if (visibleNode.sceneNode) {
        visibleNode.sceneNode.visible = false
      }
    }
    pointcloud.numVisibleNodes = 0
    pointcloud.numVisiblePoints = 0
    pointcloud.deepestVisibleLevel = 0
    pointcloud.visibleNodes = []

    // frustum in object space
    camera.updateMatrixWorld(true)
    pointcloud.updateMatrixWorld(true)
    const frustum = new THREE.Frustum()
    const viewI = camera.matrixWorldInverse
    const world = pointcloud.matrixWorld

    // use close near plane for frustum intersection
    const frustumCam = camera.clone()
    frustumCam.near = Math.min(camera.near, 0.1)
    frustumCam.updateProjectionMatrix()
    const proj = frustumCam.projectionMatrix

    const fm = new THREE.Matrix4().multiply(proj).multiply(viewI).multiply(world)
    frustum.setFromProjectionMatrix(fm)
    frustums.push(frustum)
    orthoPixelScales.push(
      camera.isOrthographicCamera ? computeOrthographicPixelScale(fm, domWidth, domHeight) : 0
    )

    // camera position in object space
    const view = camera.matrixWorld
    const worldI = world.clone().invert()
    const camMatrixObject = new THREE.Matrix4().multiply(worldI).multiply(view)
    const camObjPos = new THREE.Vector3().setFromMatrixPosition(camMatrixObject)
    camObjPositions.push(camObjPos)

    if (pointcloud.visible && pointcloud.root !== null) {
      priorityQueue.push({ pointcloud: i, node: pointcloud.root, weight: Number.MAX_VALUE })
    }

    // hide all previously visible nodes
    // if(pointcloud.root instanceof PointCloudOctreeNode){
    //	pointcloud.hideDescendants(pointcloud.root.sceneNode);
    // }
    for (let j = 0; j < pointcloud.boundingBoxNodes.length; j++) {
      pointcloud.boundingBoxNodes[j].visible = false
    }
  }

  return {
    frustums: frustums,
    camObjPositions: camObjPositions,
    orthoPixelScales: orthoPixelScales,
    priorityQueue: priorityQueue,
  }
}

/**
 * 核心可见性计算：遍历八叉树优先队列，判断节点可见性并管理加载。
 *
 * @param pointclouds - 八叉树点云列表
 * @param camera - 当前相机
 * @param lru - LRU 缓存实例
 * @param pointBudget - 全局点数预算
 * @param maxNodesLoading - 每帧最大加载节点数
 * @param viewportSize - 当前 View 的逻辑像素尺寸
 * @param progressContext - 可选帧与视口上下文，用于聚合 LOD 就绪度
 * @returns 含 visibleNodes、numVisiblePoints、lowestSpacing 等的结果对象
 */
export function updateVisibility(
  pointclouds: PointCloudOctree[],
  camera,
  lru: LRULike,
  pointBudget: number,
  maxNodesLoading: number,
  viewportSize: PointCloudVisibilityViewport,
  progressContext?: ViewLodProgressContext
) {
  const _numVisibleNodes = 0
  let numVisiblePoints = 0

  const numVisiblePointsInPointclouds = new Map(pointclouds.map((pc) => [pc, 0]))
  const lodProgressInPointclouds = new Map(
    pointclouds.map((pointcloud) => [pointcloud, { ready: 0, total: 0 }])
  )

  const visibleNodes: PointCloudOctreeNode[] = []
  const unloadedGeometry: OctreeGeometryNode[] = []
  const requiredGeometryNodes = new Set<OctreeGeometryNode>()

  let lowestSpacing = Infinity

  // calculate object space frustum and cam pos and setup priority queue
  const s = updateVisibilityStructures(pointclouds, camera, viewportSize)
  const frustums = s.frustums
  const camObjPositions = s.camObjPositions
  const orthoPixelScales = s.orthoPixelScales
  const priorityQueue = s.priorityQueue
  const debugStats = createVisibilityDebugStats()

  // 每帧加载的点云数量
  let loadedToGPUThisFrame = 0

  const _domWidth = Math.max(1, viewportSize.width)
  const domHeight = Math.max(1, viewportSize.height)

  // check if pointcloud has been transformed
  // some code will only be executed if changes have been detected
  const pointcloudTransformVersion = _pointcloudTransformVersion
  for (const pointcloud of pointclouds) {
    if (!pointcloud.visible) {
      continue
    }

    pointcloud.updateMatrixWorld(true)

    let version = pointcloudTransformVersion.get(pointcloud)
    if (!version) {
      version = { number: 0, transform: pointcloud.matrixWorld.clone() }
      pointcloudTransformVersion.set(pointcloud, version)
    } else {
      if (!version.transform.equals(pointcloud.matrixWorld)) {
        version.number++
        version.transform.copy(pointcloud.matrixWorld)

        pointcloud.dispatchEvent({
          type: 'transformation_changed',
          target: pointcloud,
        })
      }
    }
  }

  while (priorityQueue.size() > 0) {
    const element = priorityQueue.pop()
    if (!element) break
    let node = element.node
    const parent = element.parent
    const pointcloud = pointclouds[element.pointcloud]
    debugStats.visited++

    if (isOctreeGeometryNode(node)) {
      const renderNode = pointcloud.getRenderNode(node)
      if (renderNode?.isLoaded()) {
        node = renderNode
      }
    }

    // { // restrict to certain nodes for debugging
    //	let allowedNodes = ["r", "r0", "r4"];
    //	if(!allowedNodes.includes(node.name)){
    //		continue;
    //	}
    // }

    const box = node.getBoundingBox()
    const frustum = frustums[element.pointcloud]
    const camObjPos = camObjPositions[element.pointcloud]

    const insideFrustum = frustum.intersectsBox(box)
    const maxLevel = pointcloud.maxLevel || Infinity
    const level = node.getLevel()
    const exceedsGlobalBudget = numVisiblePoints + node.getNumPoints() > pointBudget
    const exceedsPointcloudBudget =
      numVisiblePointsInPointclouds.get(pointcloud)! + node.getNumPoints() > pointcloud.pointBudget
    const exceedsMaxLevel = level! >= maxLevel
    let visible = insideFrustum
    visible = visible && !exceedsGlobalBudget
    visible = visible && !exceedsPointcloudBudget
    visible = visible && !exceedsMaxLevel
    const visibleBeforeLevelForce = visible
    visible = visible || level! <= 2
    if (!insideFrustum) {
      debugStats.rejectedFrustum++
    }
    if (exceedsGlobalBudget) {
      debugStats.rejectedGlobalBudget++
    }
    if (exceedsPointcloudBudget) {
      debugStats.rejectedPointcloudBudget++
    }
    if (exceedsMaxLevel) {
      debugStats.rejectedMaxLevel++
    }
    if (!visibleBeforeLevelForce && visible) {
      debugStats.forcedLevelVisible++
    }

    const clipBoxes = pointcloud.material.clipBoxes
    if (true && clipBoxes.length > 0) {
      //node.debug = false;

      let numIntersecting = 0
      let numIntersectionVolumes = 0

      //if(node.name === "r60"){
      //	var a = 10;
      //}

      for (const clipBox of clipBoxes) {
        const pcWorldInverse = pointcloud.matrixWorld.clone().invert()
        const clipBoxMatrixWorld = clipBox.box?.matrixWorld ?? clipBox.inverse.clone().invert()
        const toPCObject = pcWorldInverse.multiply(clipBoxMatrixWorld)

        const px = new THREE.Vector3(+0.5, 0, 0).applyMatrix4(toPCObject)
        const nx = new THREE.Vector3(-0.5, 0, 0).applyMatrix4(toPCObject)
        const py = new THREE.Vector3(0, +0.5, 0).applyMatrix4(toPCObject)
        const ny = new THREE.Vector3(0, -0.5, 0).applyMatrix4(toPCObject)
        const pz = new THREE.Vector3(0, 0, +0.5).applyMatrix4(toPCObject)
        const nz = new THREE.Vector3(0, 0, -0.5).applyMatrix4(toPCObject)

        const pxN = new THREE.Vector3().subVectors(nx, px).normalize()
        const nxN = pxN.clone().multiplyScalar(-1)
        const pyN = new THREE.Vector3().subVectors(ny, py).normalize()
        const nyN = pyN.clone().multiplyScalar(-1)
        const pzN = new THREE.Vector3().subVectors(nz, pz).normalize()
        const nzN = pzN.clone().multiplyScalar(-1)

        const pxPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(pxN, px)
        const nxPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(nxN, nx)
        const pyPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(pyN, py)
        const nyPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(nyN, ny)
        const pzPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(pzN, pz)
        const nzPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(nzN, nz)

        //if(window.debugdraw !== undefined && window.debugdraw === true && node.name === "r60"){

        //	Potree.utils.debugPlane(viewer.scene.scene, pxPlane, 1, 0xFF0000);
        //	Potree.utils.debugPlane(viewer.scene.scene, nxPlane, 1, 0x990000);
        //	Potree.utils.debugPlane(viewer.scene.scene, pyPlane, 1, 0x00FF00);
        //	Potree.utils.debugPlane(viewer.scene.scene, nyPlane, 1, 0x009900);
        //	Potree.utils.debugPlane(viewer.scene.scene, pzPlane, 1, 0x0000FF);
        //	Potree.utils.debugPlane(viewer.scene.scene, nzPlane, 1, 0x000099);

        //	Potree.utils.debugBox(viewer.scene.scene, box, new THREE.Matrix4(), 0x00FF00);
        //	Potree.utils.debugBox(viewer.scene.scene, box, pointcloud.matrixWorld, 0xFF0000);
        //	Potree.utils.debugBox(viewer.scene.scene, clipBox.box.boundingBox, clipBox.box.matrixWorld, 0xFF0000);

        //	window.debugdraw = false;
        //}

        const frustum = new THREE.Frustum(pxPlane, nxPlane, pyPlane, nyPlane, pzPlane, nzPlane)
        const intersects = frustum.intersectsBox(box)

        if (intersects) {
          numIntersecting++
        }
        numIntersectionVolumes++
      }

      const insideAny = numIntersecting > 0
      const insideAll = numIntersecting === numIntersectionVolumes

      if (pointcloud.material.clipTask === ClipTask.SHOW_INSIDE) {
        if (pointcloud.material.clipMethod === ClipMethod.INSIDE_ANY && insideAny) {
          //node.debug = true
        } else if (pointcloud.material.clipMethod === ClipMethod.INSIDE_ALL && insideAll) {
          //node.debug = true;
        } else {
          visible = false
        }
      } else if (pointcloud.material.clipTask === ClipTask.SHOW_OUTSIDE) {
        //if(pointcloud.material.clipMethod === ClipMethod.INSIDE_ANY && !insideAny){
        //	//visible = true;
        //	let a = 10;
        //}else if(pointcloud.material.clipMethod === ClipMethod.INSIDE_ALL && !insideAll){
        //	//visible = true;
        //	let a = 20;
        //}else{
        //	visible = false;
        //}
      }
    }

    // ========== 新增：多边形组检查 ==========
    const clipPolygons = pointcloud.material.clipPolygons
    let isPolygonBoundaryNode = false // 标记是否为多边形边界节点
    if (visible && clipPolygons && clipPolygons.length > 0) {
      // 获取节点包围盒在世界空间的表示
      const nodeBoxWorld = box.clone().applyMatrix4(pointcloud.matrixWorld)

      // 按组组织多边形
      const groupPolygons = new Map<number, any[]>()
      const groupClipTasks = new Map<number, ClipTask>()

      for (const polygon of clipPolygons) {
        if (!polygon.initialized) continue

        const groupId = polygon.groupId || 0
        if (!groupPolygons.has(groupId)) {
          groupPolygons.set(groupId, [])
          groupClipTasks.set(groupId, polygon.clipTask)
        }
        groupPolygons.get(groupId)!.push(polygon)
      }

      // 遍历每个组进行检查
      for (const [groupId, polygons] of groupPolygons) {
        const clipTask = groupClipTasks.get(groupId)

        // 计算该组所有多边形的合并NDC空间AABB
        let groupAABB: { min: { x: number; y: number }; max: { x: number; y: number } } | null =
          null
        let groupProjViewMatrix: THREE.Matrix4 | null = null

        for (const polygon of polygons) {
          // 使用第一个多边形的投影矩阵
          if (!groupProjViewMatrix && polygon.viewMatrix && polygon.projMatrix) {
            groupProjViewMatrix = new THREE.Matrix4().multiplyMatrices(
              polygon.projMatrix,
              polygon.viewMatrix
            )
          }

          if (!groupAABB) {
            groupAABB = {
              min: { x: polygon.aabb.min.x, y: polygon.aabb.min.y },
              max: { x: polygon.aabb.max.x, y: polygon.aabb.max.y },
            }
          } else {
            groupAABB.min.x = Math.min(groupAABB.min.x, polygon.aabb.min.x)
            groupAABB.min.y = Math.min(groupAABB.min.y, polygon.aabb.min.y)
            groupAABB.max.x = Math.max(groupAABB.max.x, polygon.aabb.max.x)
            groupAABB.max.y = Math.max(groupAABB.max.y, polygon.aabb.max.y)
          }
        }

        if (!groupAABB || !groupProjViewMatrix) continue

        // 将节点包围盒的8个角点投影到NDC空间，计算节点的2D AABB
        const corners = [
          new THREE.Vector3(nodeBoxWorld.min.x, nodeBoxWorld.min.y, nodeBoxWorld.min.z),
          new THREE.Vector3(nodeBoxWorld.max.x, nodeBoxWorld.min.y, nodeBoxWorld.min.z),
          new THREE.Vector3(nodeBoxWorld.min.x, nodeBoxWorld.max.y, nodeBoxWorld.min.z),
          new THREE.Vector3(nodeBoxWorld.max.x, nodeBoxWorld.max.y, nodeBoxWorld.min.z),
          new THREE.Vector3(nodeBoxWorld.min.x, nodeBoxWorld.min.y, nodeBoxWorld.max.z),
          new THREE.Vector3(nodeBoxWorld.max.x, nodeBoxWorld.min.y, nodeBoxWorld.max.z),
          new THREE.Vector3(nodeBoxWorld.min.x, nodeBoxWorld.max.y, nodeBoxWorld.max.z),
          new THREE.Vector3(nodeBoxWorld.max.x, nodeBoxWorld.max.y, nodeBoxWorld.max.z),
        ]

        const nodeAABB = {
          min: { x: Infinity, y: Infinity },
          max: { x: -Infinity, y: -Infinity },
        }

        let hasValidProjection = true
        for (const corner of corners) {
          // applyMatrix4 会自动处理齐次坐标的透视除法
          corner.applyMatrix4(groupProjViewMatrix)

          // 检查是否在相机后面（无效投影）
          // 对于在相机后面的点，跳过剔除优化，让shader处理
          if (corner.z < -1 || corner.z > 1) {
            // 节点可能跨越相机近平面，保守处理
            hasValidProjection = false
            break
          }

          nodeAABB.min.x = Math.min(nodeAABB.min.x, corner.x)
          nodeAABB.min.y = Math.min(nodeAABB.min.y, corner.y)
          nodeAABB.max.x = Math.max(nodeAABB.max.x, corner.x)
          nodeAABB.max.y = Math.max(nodeAABB.max.y, corner.y)
        }

        // 如果投影无效，跳过节点级剔除，让shader处理
        if (!hasValidProjection) {
          isPolygonBoundaryNode = true
          continue
        }

        // 在2D NDC空间中检查相交关系
        const intersects2D = !(
          nodeAABB.max.x < groupAABB.min.x ||
          nodeAABB.min.x > groupAABB.max.x ||
          nodeAABB.max.y < groupAABB.min.y ||
          nodeAABB.min.y > groupAABB.max.y
        )

        const _fullyInside2D =
          nodeAABB.min.x >= groupAABB.min.x &&
          nodeAABB.max.x <= groupAABB.max.x &&
          nodeAABB.min.y >= groupAABB.min.y &&
          nodeAABB.max.y <= groupAABB.max.y

        const fullyOutside2D = !intersects2D

        // 根据裁切任务决定可见性
        // 注意：多边形可能不是矩形（如三角形），所以AABB内的节点不能直接剔除
        // 只有完全在AABB外的节点才能安全地进行节点级优化
        if (clipTask === ClipTask.SHOW_INSIDE) {
          // 内裁：显示多边形内的点
          // 如果节点完全在AABB外，则不可能有点在多边形内，可以剔除
          if (fullyOutside2D) {
            visible = false
            break
          }
          // 在AABB内或部分相交：需要shader精确判断
          isPolygonBoundaryNode = true
        } else if (clipTask === ClipTask.SHOW_OUTSIDE) {
          // 外裁：显示多边形外的点
          // 只有完全在AABB外的节点才能确定全部显示
          if (fullyOutside2D) {
            continue // 确定可见，检查下一个组
          }
          // 在AABB内或部分相交：需要shader精确判断，不能节点级剔除
          // 因为AABB比多边形大，节点可能部分在多边形外
          isPolygonBoundaryNode = true
        }
        // HIGHLIGHT不影响节点可见性，跳过
      }
    }

    // visible = ["r", "r0", "r06", "r060"].includes(node.name);
    // visible = ["r"].includes(node.name);

    // @ts-expect-error
    if (node.spacing) {
      // @ts-expect-error
      lowestSpacing = Math.min(lowestSpacing, node.spacing)
      // @ts-expect-error
    } else if (node.geometryNode?.spacing) {
      // @ts-expect-error
      lowestSpacing = Math.min(lowestSpacing, node.geometryNode.spacing)
    }

    if (!visible) {
      continue
    }
    debugStats.visibleCandidates++

    // 获取子节点信息（后续需要用于添加到优先队列）
    const children = node.getChildren()

    // 预算检查：仅统计实际渲染的点数，避免未上传GPU的节点消耗预算
    if (numVisiblePoints + node.getNumPoints() > pointBudget) {
      debugStats.budgetBreaks++
      break
    }
    const requiredGeometryNode = isOctreeGeometryNode(node) ? node : node.geometryNode
    if (requiredGeometryNode) {
      requiredGeometryNodes.add(requiredGeometryNode)
    }
    const lodProgress = lodProgressInPointclouds.get(pointcloud)!
    if (!isOctreeGeometryNode(node) || !node.structural) {
      lodProgress.total++
    }

    // GPU 上传 / 加载调度
    if (isOctreeGeometryNode(node) && node.structural) {
      // 纯结构节点只负责保持拓扑可遍历，不创建空渲染载荷或驻留记录。
    } else if (isOctreeGeometryNode(node)) {
      debugStats.geometryCandidates++
      if (node.isLoaded()) {
        debugStats.loadedGeometryCandidates++
      }
      if (node.isLoaded() && loadedToGPUThisFrame < 4) {
        node = pointcloud.toTreeNode(node, parent)
        loadedToGPUThisFrame++
        debugStats.gpuUploads++
      } else if (!node.loadPermanentlyFailed) {
        unloadedGeometry.push(node)
        if (node.isLoaded()) {
          debugStats.gpuLimitHits++
        } else {
          debugStats.queuedForLoad++
        }
      } else {
        debugStats.permanentlyFailed++
      }
    }
    if (isPointCloudOctreeNode(node)) {
      if (!node.geometryNode || !node.sceneNode || !node.isLoaded()) {
        continue
      }
      lru.touch(node.geometryNode)
      node.sceneNode.visible = true
      node.sceneNode.material = pointcloud.material

      visibleNodes.push(node)
      pointcloud.visibleNodes.push(node)
      lodProgress.ready++
      debugStats.treeVisible++

      // 仅对实际渲染的 TreeNode 计入可见点数
      numVisiblePoints += node.getNumPoints()
      const numVisiblePointsInPointcloud = numVisiblePointsInPointclouds.get(pointcloud)!
      numVisiblePointsInPointclouds.set(
        pointcloud,
        numVisiblePointsInPointcloud + node.getNumPoints()
      )
      pointcloud.numVisibleNodes!++
      pointcloud.numVisiblePoints! += node.getNumPoints()

      if (node._transformVersion === undefined) {
        node._transformVersion = -1
      }
      const transformVersion = pointcloudTransformVersion.get(pointcloud)
      if (transformVersion && node._transformVersion !== transformVersion.number) {
        node.sceneNode.updateMatrix()
        node.sceneNode.matrixWorld.multiplyMatrices(pointcloud.matrixWorld, node.sceneNode.matrix)
        node._transformVersion = transformVersion.number
      }

      if (pointcloud.showBoundingBox && !node.boundingBoxNode && node.getBoundingBox!) {
        const boxHelper = new Box3Helper(node.getMinimumBoundingBox(), 0x0a65b9)
        boxHelper.matrixAutoUpdate = false
        pointcloud.boundingBoxNodes.push(boxHelper)
        node.boundingBoxNode = boxHelper
        node.boundingBoxNode.matrix.copy(pointcloud.matrixWorld)
      } else if (pointcloud.showBoundingBox) {
        node.boundingBoxNode.visible = true
        node.boundingBoxNode.matrix.copy(pointcloud.matrixWorld)
      } else if (!pointcloud.showBoundingBox && node.boundingBoxNode) {
        node.boundingBoxNode.visible = false
      }
    }

    // add child nodes to priorityQueue
    // children已在前面获取，无需重复获取
    for (let i = 0; i < children.length; i++) {
      const child = children[i]

      let weight = 0
      if (camera.isPerspectiveCamera) {
        const sphere = child.getBoundingSphere()
        const center = sphere.center
        //let distance = sphere.center.distanceTo(camObjPos);

        const dx = camObjPos.x - center.x
        const dy = camObjPos.y - center.y
        const dz = camObjPos.z - center.z

        const dd = dx * dx + dy * dy + dz * dz
        const distance = Math.sqrt(dd)

        const radius = sphere.radius
        // 这里是精确的计算方式，得到节点的包围球到屏幕投影的像素半径
        const fov = (camera.fov * Math.PI) / 180
        const slope = Math.tan(fov / 2)
        const projFactor = (0.5 * domHeight) / (slope * distance)
        const screenPixelRadius = radius * projFactor

        if (screenPixelRadius < pointcloud.minimumNodePixelSize) {
          debugStats.childrenSkippedMinSize++
          continue
        }

        weight = screenPixelRadius

        if (distance - radius < 0) {
          weight = Number.MAX_VALUE
        }

        // 边界节点的子节点优先级提升，以便更快细分到精确LOD
        if (isPolygonBoundaryNode) {
          weight = Math.max(weight * 2, weight + 1000)
        }
      } else {
        const sphere = child.getBoundingSphere()
        const screenPixelRadius = sphere.radius * (orthoPixelScales[element.pointcloud] ?? 0)
        if (screenPixelRadius < pointcloud.minimumNodePixelSize) {
          debugStats.childrenSkippedMinSize++
          continue
        }

        weight = screenPixelRadius

        // 边界节点的子节点优先级提升
        if (isPolygonBoundaryNode) {
          weight = weight * 2
        }
      }

      priorityQueue.push({
        pointcloud: element.pointcloud,
        node: child,
        parent: node,
        weight: weight,
      })
      debugStats.childrenQueued++
    }
  } // end priority queue loop

  {
    // update DEM
    const maxDEMLevel = 4
    const candidates = pointclouds.filter(hasDEMUpdater)
    for (const pointcloud of candidates) {
      const updatingNodes = pointcloud.visibleNodes.filter((n) => n.getLevel() <= maxDEMLevel)
      pointcloud.dem.update(updatingNodes)
    }
  }

  const loadsStarted = startNodeLoads(unloadedGeometry, maxNodesLoading, debugStats)
  if (log.isEnabled(LogLevel.DEBUG)) {
    const now = getLoggerManager().now()
    const queueSize = unloadedGeometry.length
    if (
      shouldLogVisibilityDebugStats(debugStats, loadsStarted, queueSize) &&
      now - lastVisibilityDebugLogTime >= VISIBILITY_DEBUG_LOG_INTERVAL_MS
    ) {
      lastVisibilityDebugLogTime = now
      log.debug(
        `LOD activity, assets=${pointclouds.length}, visiblePoints=${numVisiblePoints}, treeVisible=${debugStats.treeVisible}, queuedForLoad=${debugStats.queuedForLoad}, loadsStarted=${loadsStarted}, queueSize=${queueSize}, gpuUploads=${debugStats.gpuUploads}, gpuLimitHits=${debugStats.gpuLimitHits}, loadBlockedDisposed=${debugStats.loadBlockedDisposed}, loadBlockedRetry=${debugStats.loadBlockedRetry}, loadBlockedConcurrency=${debugStats.loadBlockedConcurrency}, loadBlockedNoLoader=${debugStats.loadBlockedNoLoader}, loadBlockedUnknown=${debugStats.loadBlockedUnknown}, rejectedBudget=${debugStats.rejectedGlobalBudget + debugStats.rejectedPointcloudBudget}, budgetBreaks=${debugStats.budgetBreaks}, permanentlyFailed=${debugStats.permanentlyFailed}, pointBudget=${pointBudget}`
      )
    }
  }

  // Sort visible nodes by distance from back to front for correct alpha blending.
  for (let i = 0; i < pointclouds.length; i++) {
    const pointcloud = pointclouds[i]
    const material = pointcloud.material

    if (material.opacity < 1.0 && material.transparent) {
      const cameraPosition = camObjPositions[i]
      pointcloud.visibleNodes.sort((a, b) => {
        const distA = a.getBoundingSphere().center.distanceTo(cameraPosition)
        const distB = b.getBoundingSphere().center.distanceTo(cameraPosition)
        return distB - distA
      })
    }
  }

  for (const pointcloud of pointclouds) {
    const lodProgress = lodProgressInPointclouds.get(pointcloud)!
    pointcloud.updateViewLodProgress(lodProgress.ready, lodProgress.total, progressContext)
  }

  return {
    visibleNodes: visibleNodes,
    numVisiblePoints: numVisiblePoints,
    lowestSpacing: lowestSpacing,
    activity: {
      queuedForLoad: debugStats.queuedForLoad,
      loadsStarted,
      gpuUploads: debugStats.gpuUploads,
      gpuUploadBacklog: debugStats.gpuLimitHits + debugStats.loadedWaitingForParent,
      loadedWaitingForParent: debugStats.loadedWaitingForParent,
      queuedWaitingForParent: debugStats.queuedWaitingForParent,
      queueSize: unloadedGeometry.length,
    } satisfies VisibilityActivitySnapshot,
    requiredGeometryNodes,
    loadCandidates: unloadedGeometry,
  }
}
